import React from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Lightbulb, 
  Target, 
  Sparkles,
  Users,
  CheckCircle,
  Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useApp } from '@/context/AppContext';

export const HomePage = () => {
  const { t, hasProgress, isCompleted } = useApp();

  const features = [
    {
      icon: Lightbulb,
      titleKey: 'featureReflexive',
      descKey: 'featureReflexiveDesc',
      color: 'bg-primary/10 text-primary'
    },
    {
      icon: Target,
      titleKey: 'featurePractical',
      descKey: 'featurePracticalDesc',
      color: 'bg-secondary/10 text-secondary'
    },
    {
      icon: Sparkles,
      titleKey: 'featureAction',
      descKey: 'featureActionDesc',
      color: 'bg-accent/10 text-accent'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24 lg:py-32">
        {/* Background Decorations */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl mx-auto text-center"
          >
            {/* Badge */}
            <motion.div variants={itemVariants} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                <Users className="h-4 w-4" />
                LAB XXI - Alza Tu Voz
              </span>
            </motion.div>

            {/* Title */}
            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground mb-6 leading-tight"
            >
              {t('welcomeTitle')}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              variants={itemVariants}
              className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed"
            >
              {t('welcomeSubtitle')}
            </motion.p>

            {/* Description */}
            <motion.p
              variants={itemVariants}
              className="text-base text-muted-foreground mb-10 max-w-2xl mx-auto"
            >
              {t('welcomeDescription')}
            </motion.p>

            {/* CTAs */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {isCompleted ? (
                <>
                  <Button
                    asChild
                    size="lg"
                    className="btn-brand-primary text-base px-8 py-6 shadow-glow"
                  >
                    <a href="/results">
                      <CheckCircle className="mr-2 h-5 w-5" />
                      {t('viewResults')}
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className="btn-brand-outline text-base px-8 py-6"
                  >
                    <a href="/action-plan">
                      {t('actionPlan')}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </a>
                  </Button>
                </>
              ) : hasProgress ? (
                <>
                  <Button
                    asChild
                    size="lg"
                    className="btn-brand-primary text-base px-8 py-6 shadow-glow"
                  >
                    <a href="/questionnaire">
                      <Play className="mr-2 h-5 w-5" />
                      {t('continueProgress')}
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className="btn-brand-outline text-base px-8 py-6"
                  >
                    <a href="/questionnaire">
                      {t('startDiagnosis')}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </a>
                  </Button>
                </>
              ) : (
                <Button
                  asChild
                  size="lg"
                  className="btn-brand-primary text-base px-10 py-6 shadow-glow"
                >
                  <a href="/questionnaire">
                    {t('startDiagnosis')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </Button>
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8"
          >
            {features.map((feature, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Card className="card-brand h-full border-0 bg-card">
                  <CardContent className="p-8 flex flex-col items-center text-center">
                    <div className={`w-16 h-16 rounded-2xl ${feature.color} flex items-center justify-center mb-6`}>
                      <feature.icon className="h-8 w-8" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-3">
                      {t(feature.titleKey)}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {t(feature.descKey)}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              {t('howItWorksTitle')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('howItWorksSubtitle')}
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-4 gap-6"
          >
            {[
              { step: '1', titleKey: 'step1Title', descKey: 'step1Desc' },
              { step: '2', titleKey: 'step2Title', descKey: 'step2Desc' },
              { step: '3', titleKey: 'step3Title', descKey: 'step3Desc' },
              { step: '4', titleKey: 'step4Title', descKey: 'step4Desc' }
            ].map((item, index) => (
              <motion.div key={index} variants={itemVariants}>
                <div className="relative p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground font-bold text-xl flex items-center justify-center mx-auto mb-4 shadow-glow">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{t(item.titleKey)}</h3>
                  <p className="text-sm text-muted-foreground">{t(item.descKey)}</p>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-10 right-0 translate-x-1/2 w-8 h-0.5 bg-border" />
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
